// =============================================================================
// TAG PRESETS — Constantes centralisées pour les tags EVA
// Garantit la cohérence des labels, sévérités et icônes dans toute l'application.
// =============================================================================

// -----------------------------------------------------------------------------
// Types
// -----------------------------------------------------------------------------

/** Sévérités sémantiques disponibles pour les tags */
export type TagSeverity =
  | "primary"
  | "secondary"
  | "success"
  | "warning"
  | "danger"
  | "info"
  | "purple"
  | "pink"
  | "teal"
  | "olive"
  | "terracotta";

/** Variantes visuelles disponibles pour les tags */
export type TagVariant = "filled" | "outlined" | "soft";

/** Configuration complète d'un tag preset */
export interface TagPreset {
  /** Label affiché dans le tag (en français avec accents) */
  label: string;
  /** Sévérité sémantique du tag */
  severity: TagSeverity;
  /** Icône PrimeNG optionnelle (ex: "heroCheck") */
  icon?: string;
  /** Variante visuelle optionnelle (défaut: soft) */
  variant?: TagVariant;
}

// -----------------------------------------------------------------------------
// CUSTOMER_TYPE — Type de client
// -----------------------------------------------------------------------------

/** Tags pour le type de client (entreprise ou particulier) */
export const CUSTOMER_TYPE_TAGS: Record<string, TagPreset> = {
  COMPANY: { label: "Entreprise", severity: "olive" },
  INDIVIDUAL: { label: "Particulier", severity: "purple" },
};

// -----------------------------------------------------------------------------
// CUSTOMER_STATUS — Relation client
// -----------------------------------------------------------------------------

/** Tags pour la relation client (client acquis ou prospect) */
export const CUSTOMER_STATUS_TAGS: Record<string, TagPreset> = {
  CLIENT: { label: "Client", severity: "teal" },
  PROSPECT: { label: "Prospect", severity: "terracotta" },
};

// -----------------------------------------------------------------------------
// ACTIVE_STATUS — Statut actif/inactif
// Utilisé pour : clients, contacts, employés, fournisseurs, articles, sites, modèles d'offres
// -----------------------------------------------------------------------------

/** Tags pour le statut actif/inactif (universel) */
export const ACTIVE_STATUS_TAGS: Record<string, TagPreset> = {
  ENABLED: { label: "Actif", severity: "success" },
  DISABLED: { label: "Inactif", severity: "danger" },
};

// -----------------------------------------------------------------------------
// OFFER_STATUS — Statuts d'offres
// Utilisé dans offer-v2 et offer
// -----------------------------------------------------------------------------

/** Tags pour les statuts d'offres (workflow complet) */
export const OFFER_STATUS_TAGS: Record<string, TagPreset> = {
  ON_GOING: { label: "Brouillon", severity: "warning", icon: "heroDocument" },
  SENT: { label: "Envoyée", severity: "info", icon: "heroPaperAirplane" },
  VALIDATED: { label: "Acceptée", severity: "success", icon: "heroCheck" },
  CANCELLED: { label: "Abandonnée", severity: "danger", icon: "heroXMark" },
  TEMPLATE: { label: "Modèle actif", severity: "success" },
  TEMPLATE_INACTIVE: { label: "Modèle archivé", severity: "danger" },
};

// -----------------------------------------------------------------------------
// INVOICE_STATUS — Statuts des factures émises
// -----------------------------------------------------------------------------

/** Tags pour les statuts de factures émises (cycle de vie complet) */
export const INVOICE_STATUS_TAGS: Record<string, TagPreset> = {
  DRAFT: { label: "Brouillon", severity: "warning" },
  VALIDATED: { label: "Validée", severity: "success" },
  CANCELLED: { label: "Annulée", severity: "danger" },
  SENT: { label: "Envoyée", severity: "info" },
  PAID: { label: "Payée", severity: "secondary" },
  ON_GOING: { label: "Attente de paiement", severity: "warning" },
  ACCOUNTED: { label: "Mise en comptabilité", severity: "secondary" },
};

// -----------------------------------------------------------------------------
// PURCHASE_ORDER_STATUS — Statuts des bons de commande
// -----------------------------------------------------------------------------

/** Tags pour les statuts de bons de commande */
export const PURCHASE_ORDER_STATUS_TAGS: Record<string, TagPreset> = {
  ON_GOING: { label: "En cours", severity: "warning" },
  FINISHED: { label: "Clôturé", severity: "success" },
  CANCELLED: { label: "Abandonné", severity: "danger" },
};

/** Tags pour les types de bon de commande (Matériel vs Main d'oeuvre) */
export const PURCHASE_ORDER_TYPE_TAGS: Record<string, TagPreset> = {
  MATERIAL: { label: "Matériel", severity: "olive" },
  WORKFORCE: { label: "Main d'oeuvre", severity: "purple" },
};

/** Tags pour les types de livraison */
export const PURCHASE_ORDER_DELIVERY_TAGS: Record<string, TagPreset> = {
  PROJECT_SITE: { label: "Sur chantier", severity: "primary" },
  WORKSHOP: { label: "Atelier", severity: "teal" },
  SHOP: { label: "Pris au magasin", severity: "warning" },
  CUSTOM: { label: "Autre", severity: "secondary" },
};

/** Tags pour les sous-types main d'oeuvre */
export const PURCHASE_ORDER_WORKFORCE_TAGS: Record<string, TagPreset> = {
  EMPLOYEE: { label: "Collaborateur", severity: "info" },
  SUBCONTRACTOR: { label: "Sous-traitant", severity: "terracotta" },
};

// -----------------------------------------------------------------------------
// RECEIVED_INVOICE_STATUS — Statuts des factures reçues
// -----------------------------------------------------------------------------

/** Tags pour les statuts de factures reçues (fournisseurs) */
export const RECEIVED_INVOICE_STATUS_TAGS: Record<string, TagPreset> = {
  ON_GOING: { label: "En attente", severity: "warning" },
  PAID: { label: "Payée", severity: "success" },
  CANCELLED: { label: "Annulée", severity: "danger" },
};

// -----------------------------------------------------------------------------
// PROJECT_STATUS — Statuts de projets/chantiers
// -----------------------------------------------------------------------------

/** Tags pour les statuts de projets et chantiers */
export const PROJECT_STATUS_TAGS: Record<string, TagPreset> = {
  DRAFT: { label: "Brouillon", severity: "success" },
  ON_GOING: { label: "En cours", severity: "warning" },
  WAITING: { label: "En attente", severity: "primary" },
  TERMINATED: { label: "Terminé", severity: "secondary" },
  CANCELLED: { label: "Abandonné", severity: "danger" },
};

// -----------------------------------------------------------------------------
// INTERVENTION_STATUS — Statuts d'interventions
// -----------------------------------------------------------------------------

/** Tags pour les statuts d'interventions terrain */
export const INTERVENTION_STATUS_TAGS: Record<string, TagPreset> = {
  ONGOING: { label: "En cours", severity: "info" },
  CANCELLED: { label: "Abandonnée", severity: "danger" },
  TERMINATED: { label: "Terminée", severity: "success" },
};

// -----------------------------------------------------------------------------
// TIMESHEET_STATUS — Statuts d'activités/planning
// -----------------------------------------------------------------------------

/** Tags pour les statuts de saisie des heures et planning */
export const TIMESHEET_STATUS_TAGS: Record<string, TagPreset> = {
  DRAFT: { label: "Brouillon", severity: "secondary" },
  PENDING: { label: "Attente de validation", severity: "warning" },
  VALIDATED: { label: "Validée", severity: "success" },
  REJECTED: { label: "Refusée", severity: "danger" },
  NONE: { label: "Pas de données", severity: "secondary" },
};

// -----------------------------------------------------------------------------
// DOCUMENT_TYPE — Types de documents
// -----------------------------------------------------------------------------

/** Tags pour les types de documents (home) */
export const DOCUMENT_TYPE_TAGS: Record<string, TagPreset> = {
  OFFER: { label: "Offre", severity: "primary" },
  INVOICE: { label: "Facture", severity: "warning" },
};

// -----------------------------------------------------------------------------
// EMPLOYEE_ROLE — Rôles employés
// -----------------------------------------------------------------------------

/** Tags pour les rôles employés (permissions et accès) */
export const EMPLOYEE_ROLE_TAGS: Record<string, TagPreset> = {
  ADMIN: { label: "Admin", severity: "warning" },
  OFFICE: { label: "Bureau", severity: "info" },
  FIDUCIARY: { label: "Fiduciaire", severity: "secondary" },
};

// -----------------------------------------------------------------------------
// SUBSCRIPTION_STATUS — Statuts d'abonnement
// -----------------------------------------------------------------------------

/** Tags pour le statut de l'abonnement EVA */
export const SUBSCRIPTION_STATUS_TAGS: Record<string, TagPreset> = {
  ON_TRIAL: { label: "Version d'essai", severity: "secondary" },
  ACTIVE: { label: "Active", severity: "success" },
};

// -----------------------------------------------------------------------------
// ARTICLE_TYPE — Types d'articles
// -----------------------------------------------------------------------------

/** Tags pour les types d'articles du catalogue */
export const ARTICLE_TYPE_TAGS: Record<string, TagPreset> = {
  TEXT: { label: "Texte", severity: "warning" },
  PACK: { label: "Pack", severity: "secondary" },
};

// -----------------------------------------------------------------------------
// SERVICE_NATURE — Nature & groupe d'un article (catalogue)
// Labels dynamiques : override `label` à l'usage, conserver severity + icon.
// Couleurs différenciées des ARTICLE_TYPE_TAGS (Matériel, Main d'œuvre, etc.).
// -----------------------------------------------------------------------------

/** Tags pour la nature et le groupe d'un service (catalogue d'articles) */
export const SERVICE_NATURE_TAGS: Record<string, TagPreset> = {
  NATURE: { label: "Nature", severity: "teal", icon: "heroTag" },
  GROUPE: { label: "Groupe", severity: "purple", icon: "heroFolder" },
};

// -----------------------------------------------------------------------------
// ACCOUNTING_TYPE — Types comptables
// -----------------------------------------------------------------------------

/** Tags pour les types comptables (recettes et dépenses) */
export const ACCOUNTING_TYPE_TAGS: Record<string, TagPreset> = {
  INCOME: { label: "Recette", severity: "success" },
  EXPENSE: { label: "Dépense", severity: "danger" },
};

// -----------------------------------------------------------------------------
// SUPPLIER_RELATION — Types de relation fournisseur
// Utilisé dans supplier-list
// -----------------------------------------------------------------------------

/** Tags pour les types de relation fournisseur */
export const SUPPLIER_RELATION_TAGS: Record<string, TagPreset> = {
  SUPPLIER: { label: "Fournisseur", severity: "info" },
  SUBCONTRACTOR: { label: "Sous-traitant", severity: "warning" },
  ADMINISTRATION: { label: "Administration", severity: "secondary" },
  TRANSPORTER: { label: "Transporteur", severity: "success" },
};

// -----------------------------------------------------------------------------
// BILLING_MODE — Modes de facturation
// -----------------------------------------------------------------------------

/** Tags pour les modes de facturation (contrats d'intervention) */
export const BILLING_MODE_TAGS: Record<string, TagPreset> = {
  ANTICIPATED: { label: "Anticipée", severity: "info" },
  ON_COMPLETION: { label: "À la réalisation", severity: "warning" },
};

// -----------------------------------------------------------------------------
// PRIORITY — Niveaux de priorité
// -----------------------------------------------------------------------------

/** Tags pour les niveaux de priorité des tâches */
export const PRIORITY_TAGS: Record<string, TagPreset> = {
  HIGH: { label: "Urgent", severity: "danger", icon: "heroExclamationTriangle", variant: "filled" },
  NORMAL: { label: "Normal", severity: "primary", variant: "filled" },
  LOW: { label: "Basse", severity: "secondary", variant: "filled" },
};

// -----------------------------------------------------------------------------
// FINANCIAL_DATA_TYPE — Nature d'une dépense ou recette
// Utilisé dans les tables de finances projet (dépenses/recettes)
// -----------------------------------------------------------------------------

/** Tags pour qualifier la nature d'une ligne financière projet */
export const FINANCIAL_DATA_TYPE_TAGS: Record<string, TagPreset> = {
  WORKFORCE: { label: "Main d'œuvre", severity: "purple" },
  PURCHASE: { label: "Achat", severity: "terracotta" },
  MISC: { label: "Divers", severity: "primary" },
  OFFER: { label: "Offre", severity: "olive" },
  INVOICE: { label: "Facture", severity: "info" },
  CONTRACT: { label: "Contrat", severity: "teal" },
};

// -----------------------------------------------------------------------------
// EVA_OBJECT — Identifiant visuel des modules et objets EVA
// -----------------------------------------------------------------------------

/** Tags pour identifier les modules et objets métier de l'application */
export const EVA_OBJECT_TAGS: Record<string, TagPreset> = {
  CHANTIER: { label: "Chantier", severity: "primary", icon: "heroBuildingOffice" },
  INTERVENTION: { label: "Intervention", severity: "teal", icon: "heroWrench" },
  CLIENT: { label: "Client", severity: "info", icon: "heroUsers" },
  CONTRAT: { label: "Contrat", severity: "teal", icon: "heroDocument" },
  SITE: { label: "Site", severity: "info", icon: "heroMapPin" },
  OFFRE: { label: "Offre", severity: "olive", icon: "heroPaperAirplane" },
  FACTURE_CLIENT: { label: "Facture client", severity: "success", icon: "heroCreditCard" },
  CATALOGUE: { label: "Catalogue", severity: "purple", icon: "heroSquares2x2" },
  ARTICLE: { label: "Article", severity: "purple", icon: "heroCube" },
  PACK: { label: "Pack", severity: "secondary", icon: "heroCube" },
  BON_COMMANDE: { label: "Bon de commande", severity: "warning", icon: "heroShoppingCart" },
  FOURNISSEUR: { label: "Fournisseur", severity: "info", icon: "heroTruck" },
  FACTURE_ACHAT: { label: "Facture achats", severity: "warning", icon: "heroCreditCard" },
  COLLABORATEUR: { label: "Collaborateur", severity: "terracotta", icon: "heroUser" },
  CONTACT: { label: "Contact", severity: "info", icon: "heroIdentification" },
  NOTE_FRAIS: { label: "Note de frais", severity: "warning", icon: "heroTag" },
};

// -----------------------------------------------------------------------------
// Helper
// -----------------------------------------------------------------------------

/**
 * Récupère un preset de tag. Retourne un fallback si la clé n'existe pas.
 * @param presets - Map de presets (ex: CUSTOMER_TYPE_TAGS)
 * @param key - Clé métier (ex: "COMPANY")
 * @returns Le preset correspondant, ou un fallback avec la clé comme label
 */
export function getTagPreset(presets: Record<string, TagPreset>, key: string): TagPreset {
  return presets[key] ?? { label: key, severity: "secondary" };
}

// -----------------------------------------------------------------------------
// TAG_PRESETS — Objet regroupant tous les presets
// Facilite l'import unique : import { TAG_PRESETS } from 'shared/constants/tag-presets';
// -----------------------------------------------------------------------------

/** Objet regroupant tous les presets de tags par catégorie métier */
export const TAG_PRESETS = {
  customerType: CUSTOMER_TYPE_TAGS,
  customerStatus: CUSTOMER_STATUS_TAGS,
  activeStatus: ACTIVE_STATUS_TAGS,
  supplierRelation: SUPPLIER_RELATION_TAGS,
  offerStatus: OFFER_STATUS_TAGS,
  invoiceStatus: INVOICE_STATUS_TAGS,
  receivedInvoiceStatus: RECEIVED_INVOICE_STATUS_TAGS,
  projectStatus: PROJECT_STATUS_TAGS,
  interventionStatus: INTERVENTION_STATUS_TAGS,
  timesheetStatus: TIMESHEET_STATUS_TAGS,
  documentType: DOCUMENT_TYPE_TAGS,
  employeeRole: EMPLOYEE_ROLE_TAGS,
  subscriptionStatus: SUBSCRIPTION_STATUS_TAGS,
  articleType: ARTICLE_TYPE_TAGS,
  serviceNature: SERVICE_NATURE_TAGS,
  accountingType: ACCOUNTING_TYPE_TAGS,
  billingMode: BILLING_MODE_TAGS,
  priority: PRIORITY_TAGS,
  evaObject: EVA_OBJECT_TAGS,
  financialDataType: FINANCIAL_DATA_TYPE_TAGS,
};
